import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Grid, Box, CircularProgress, Typography } from '@mui/material';
import Dropzone from 'react-dropzone';
import { validateSelectedAssetFile } from '../../utils/file-validations.util';
import { uploadResourceToBunnyCDN } from '../../services/content.service';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';

const AssetUpload = ({ title, resourceUrl, resourceDataCallback }) => {
  const allowedExtensions = /\.pdf$/i;
  const [selectedFilesObj, setSelectedFilesObj] = useState(null);
  const [showUploading, setShowUploading] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handleSelectFiles = async (files) => {
    setSnackbarInfo({ ...snackbarInfo, show: false });
    setSelectedFilesObj(files);

    const validationError = validateSelectedAssetFile(files, allowedExtensions);
    if (validationError) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'error',
        message: validationError,
      });
    }
  };

  const handleUploadFiles = async () => {
    setSnackbarInfo({ ...snackbarInfo, show: false });

    const validationError = validateSelectedAssetFile(
      selectedFilesObj,
      allowedExtensions
    );
    if (validationError) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: validationError,
        });
      }, 100);
    } else {
      setShowUploading(true);
      const assetFormData = new FormData();
      assetFormData.append('file', selectedFilesObj[0]);
      try {
        const cdnResponse = await uploadResourceToBunnyCDN(assetFormData);

        if (cdnResponse.data?.success) {
          resourceDataCallback(cdnResponse.data);
          setSelectedFilesObj(null);
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: `${title} Uploaded Successfully!`,
          });
        } else {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'error',
            message: 'Something went wrong! Please try again.',
          });
        }
      } catch {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: 'Something went wrong! Please try again.',
        });
      }

      setShowUploading(false);
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sm={4}
          md={4}
          sx={{
            whiteSpace: 'unset',
            wordBreak: 'break-all',
          }}
        >
          <Typography variant="button" display="block" mt={1}>
            {`${title} URL:`}
          </Typography>
          <Typography variant="caption">{resourceUrl}</Typography>
        </Grid>

        <Grid item xs={12} sm={8} md={8}>
          <Grid item xs={12} sm={12} md={12}>
            <Dropzone
              onDrop={(acceptedFiles) => handleSelectFiles(acceptedFiles)}
              disabled={showUploading}
            >
              {({ getRootProps, getInputProps }) => (
                <section>
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />

                    <Box
                      component="section"
                      sx={{
                        p: 1,
                        border: '1px dashed grey',
                        borderRadius: '20px',
                        width: '100%',
                        marginTop: 1,
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={12}>
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center',
                            }}
                          >
                            <Iconify
                              icon="mdi:cloud-upload-outline"
                              width={42}
                              sx={{ color: 'text.secondary' }}
                            />
                            Drag and drop file here, or click the button below
                          </Grid>

                          <Box
                            display="flex"
                            justifyContent="center"
                            color="primary.main"
                            mt={1}
                          >
                            <Typography variant="body2" display="block">
                              {selectedFilesObj?.[0]?.name || ''}
                            </Typography>
                          </Box>

                          <Box display="flex" justifyContent="center" p={1}>
                            <Button
                              component="label"
                              variant="contained"
                              sx={{
                                backgroundColor: '#808080',
                              }}
                              size="small"
                              disabled={showUploading}
                            >
                              {`Browse ${title}`}
                            </Button>
                          </Box>

                          <Grid
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" display="block">
                              Supported formats: PDF
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  </div>
                </section>
              )}
            </Dropzone>
          </Grid>

          <Box display="flex" justifyContent="center" p={1}>
            {!showUploading ? (
              <Button
                color="primary"
                variant="contained"
                size="small"
                disabled={showUploading}
                onClick={() => handleUploadFiles()}
                startIcon={<Iconify icon="mdi:cloud-upload" />}
              >
                {`Upload ${title}`}
              </Button>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                padding="4px 10px"
                borderRadius="6px"
                sx={{
                  backgroundColor: '#919eab3d',
                  color: '#919eabcc',
                }}
                fontSize={14}
              >
                <CircularProgress
                  disableShrink
                  size="14px"
                  sx={{ marginRight: 1, color: '#919eabcc' }}
                />
                <span>Uploading...</span>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

AssetUpload.propTypes = {
  title: PropTypes.string.isRequired,
  resourceUrl: PropTypes.string.isRequired,
  resourceDataCallback: PropTypes.func.isRequired,
};

export default AssetUpload;
